import { useEffectOnce } from 'react-use';
import {
  DraftOfferProperties,
  OfferBuilderVariant,
  RateInformations,
  useOfferBuilderContext,
} from '@/enterprise/components/OfferBuilder/OfferBuilderContext';
import { PRICING_RATE_TYPE_MAP } from '@/enterprise/components/OfferBuilder/utils';
import { DraftOfferType, DraftType, OfferFrequency, RateSourceKind } from '@/enterprise/data/useDraftOffer';
import usePricingSuggestion, { type CombinedPricingSuggestion } from '@/enterprise/data/usePricingSuggestion';

/**
 * True when review should refetch Pricing Service rates and overwrite the stored draft quote.
 *
 * `fixed` keeps the SEA-stored quote (CMT-configured rates). `standard`, `promotion`,
 * and a missing rateSourceKind keep the current refresh path.
 */
export const shouldHydrateDraftRates = (
  builderVariant: OfferBuilderVariant,
  draftOfferProperties: DraftOfferProperties | undefined
): boolean => {
  // Only draft review hydrates. Standard and acquisition builders already have live rates.
  if (builderVariant !== 'draft') {
    return false;
  }

  // SRM-initiated drafts keep the rates the SRM saved. Actionable drafts were generated earlier
  // and may be stale by the time the supplier opens review.
  if (draftOfferProperties?.draftType !== DraftType.ACTIONABLE_OFFER) {
    return false;
  }

  // Pricing Service only returns refreshable Preferred / Express Accept / recurring PD quotes for this path.
  const isRefreshableOfferType =
    draftOfferProperties.offerType === DraftOfferType.EXPRESS_ACCEPT ||
    draftOfferProperties.offerType === DraftOfferType.PREFERRED ||
    (draftOfferProperties.offerType === DraftOfferType.PRICE_DISCOVERY &&
      draftOfferProperties.frequency === OfferFrequency.RECURRING);

  if (!isRefreshableOfferType) {
    return false;
  }

  // CMT-configured rates must stay locked. `standard`, `promotion`, and a missing kind reprice.
  return draftOfferProperties.pricingModifiers?.rateSourceKind !== RateSourceKind.FIXED;
};

/**
 * Refetches fresh pricing for PREFERRED and EXPRESS_ACCEPT actionable offer drafts.
 *
 * Passes pricingModifiers from the draft to ensure correct rate bumping is applied
 * when displaying offers as part of a 'preferred upsell' campaign.
 *
 * Only mount this when `shouldHydrateDraftRates` is true.
 */
const HydrateDraftRates = () => {
  const {
    dispatch,
    state: { selected, frequency, draftOfferProperties },
  } = useOfferBuilderContext();

  const {
    draftType,
    offerType,
    frequency: draftFrequency,
    pricingModifiers: draftModifiers,
  } = draftOfferProperties ?? {};
  const isActionable = draftType === DraftType.ACTIONABLE_OFFER;
  const isPreferred = offerType === DraftOfferType.PREFERRED;
  const isExpress = offerType === DraftOfferType.EXPRESS_ACCEPT;
  const isRecurringPD = offerType === DraftOfferType.PRICE_DISCOVERY && draftFrequency === OfferFrequency.RECURRING;

  // Always explicitly set preferredCompetitionBump so SEA knows whether to apply rate bumping
  const pricingModifiers = {
    preferredCompetitionBump: draftModifiers?.preferredCompetitionBump ?? false,
  };

  const { data: pricingData } = usePricingSuggestion({
    takerMarkets: selected,
    isActionableOffer: isActionable,
    includePreferred: isPreferred,
    pricingModifiers,
  });

  const rateType = pricingData?.overallOptimalRateType;

  useEffectOnce(() => {
    if (!draftOfferProperties || !pricingData || !frequency) return;

    const getMarketPricing = (marketUuid: string, divisionUuid: string) =>
      pricingData.supplierMarkets.find((p) => p.marketUuid === marketUuid && p.supplierDivisionUuid === divisionUuid);

    // PREFERRED rates come from preferred.oneTime structure
    const getPreferredRate = (marketPricing: CombinedPricingSuggestion['supplierMarkets'][number]) => {
      const rate = marketPricing?.preferred?.oneTime;
      if (!rate?.rate || !rate?.rateType) return null;
      return {
        rate: rate.rate,
        rateType: rate.rateType as 'APR' | 'DISC',
      };
    };

    // EXPRESS_ACCEPT rates come from apr/discount.expressAccept.trendingRate.
    // When preferredCompetitionBump is true, force APR to match PREFERRED comparison.
    const getExpressRate = (marketPricing: CombinedPricingSuggestion['supplierMarkets'][number]) => {
      const resolvedRateType = pricingModifiers.preferredCompetitionBump ? 'APR' : (rateType as 'APR' | 'DISC');
      if (!resolvedRateType) return null;

      const key = PRICING_RATE_TYPE_MAP[resolvedRateType];
      const suggestion = marketPricing?.[key]?.expressAccept;
      if (!suggestion?.trendingRate) return null;

      return {
        rate: suggestion.trendingRate,
        rateType: resolvedRateType,
      };
    };

    // RECURRING PRICE_DISCOVERY rates come from apr/discount.recurring.trendingRate.
    // Follows overallOptimalRateType with no preferred-competition APR force.
    const getRecurringPDRate = (marketPricing: CombinedPricingSuggestion['supplierMarkets'][number]) => {
      const resolvedRateType = rateType as 'APR' | 'DISC';
      if (!resolvedRateType) return null;

      const key = PRICING_RATE_TYPE_MAP[resolvedRateType];
      const suggestion = marketPricing?.[key]?.recurring;
      if (!suggestion?.trendingRate) return null;

      return {
        rate: suggestion.trendingRate,
        rateType: resolvedRateType,
      };
    };

    const rateInformations: RateInformations[] = selected
      .map((market) => {
        const marketPricing = getMarketPricing(market.marketUuid, market.takerDivisionUuid);
        if (!marketPricing) return null;

        const rateInfo = isPreferred
          ? getPreferredRate(marketPricing)
          : isExpress
          ? getExpressRate(marketPricing)
          : isRecurringPD
          ? getRecurringPDRate(marketPricing)
          : null;

        if (!rateInfo) return null;

        return {
          marketUuid: market.marketUuid,
          supplierDivisionUuid: market.takerDivisionUuid,
          rateInformation: rateInfo,
        };
      })
      .filter(Boolean) as RateInformations[];

    if (!rateInformations.length) return;

    dispatch({
      type: 'HYDRATE_DRAFT_OFFER_RATE_INFORMATIONS',
      payload: {
        rateInformations,
        rateType: rateInformations[0].rateInformation.rateType as 'APR' | 'DISC',
      },
    });
  });

  return null;
};

export default HydrateDraftRates;
